import { SITE_CONFIG } from '../config';

export const assistantEnabled = SITE_CONFIG.ASSISTANT?.ENABLED === true;
const base = SITE_CONFIG.ASSISTANT?.API_BASE || 'https://tophamtechbe.com/llm/v1/assistant';
const visitors = {voice: {}, discovery: {}};
const discoveryPaths = new Set(['/discovery/config','/map-places','/nearby','/route','/showings/prepare','/showings/request']);
let memoryIdentity;
const identityKey = `assistant-browser-identity:${SITE_CONFIG.SITE_ID}`;
function browserIdentity() {
  try { return localStorage.getItem(identityKey) || memoryIdentity; }
  catch { return memoryIdentity; }
}

export class AssistantRequestError extends Error {
  constructor(message, response) {
    super(message);
    this.name = 'AssistantRequestError';
    this.status = response.status;
    this.code = response.headers?.get('X-Voice-Error-Code') || '';
    const absolute = response.headers?.get('X-Voice-Retry-At');
    const retry = response.headers?.get('Retry-After');
    const timestamp = absolute ? Date.parse(absolute) : NaN;
    const seconds = retry && /^\d+(?:\.\d+)?$/.test(retry.trim()) ? Number(retry) : NaN;
    const fallback = Number.isFinite(seconds) ? Date.now() + seconds * 1000 : retry ? Date.parse(retry) : NaN;
    const candidate = Number.isFinite(timestamp) ? timestamp : fallback;
    this.retryAt = Number.isFinite(candidate) && Number.isFinite(new Date(candidate).getTime()) ? candidate : null;
  }
}

export async function readAssistantResponse(response) {
  if (!response.ok) {
    let data;
    try { data = await response.json(); } catch { /* Keep a useful fallback. */ }
    throw new AssistantRequestError(typeof data?.detail === 'string' ? data.detail : response.status === 429 ? 'Too many requests. Please try again later.' : 'This action could not finish. Please try again.', response);
  }
  return response;
}

export async function getAssistantVisitor({discoveryOnly=false} = {}) {
  const state = visitors[discoveryOnly ? 'discovery' : 'voice'];
  if (state.visitor && state.visitor.expires > Date.now() + 30000) return state.visitor;
  if (!state.request) {
    state.request = fetch(`${base}/visitor`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ site_id: SITE_CONFIG.SITE_ID, individual_visitor: true, ...(discoveryOnly ? {discovery_only:true} : {}), ...(browserIdentity() ? { browser_identity: browserIdentity() } : {}) })
    }).then(readAssistantResponse).then(r => r.json()).then(data => {
      if (data.browser_identity) {
        memoryIdentity = data.browser_identity;
        try { localStorage.setItem(identityKey, memoryIdentity); } catch { /* Private browsing keeps it in memory. */ }
      }
      state.visitor = { ...data, expires: Date.now() + data.expires_in * 1000 };
      return state.visitor;
    }).finally(() => { state.request = null; });
  }
  return state.request;
}

export async function assistantRequest(path, body, { signal, blob = false, keepalive = false } = {}) {
  const discoveryOnly = discoveryPaths.has(path);
  const { token } = await getAssistantVisitor({discoveryOnly});
  const response = await fetch(`${base}${path}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body), signal, keepalive
  });
  if (response.status === 401) visitors[discoveryOnly ? 'discovery' : 'voice'].visitor = null;
  await readAssistantResponse(response);
  return blob ? response.blob() : response.json();
}

// Deliberately selected public fields, not the raw MLS payload.
const publicFields = new Set('listing_key address street_address full_address street_number street_dir_prefix street_name street_suffix street_dir_suffix unit_number city state postal_code current_price list_price price bedrooms_total bedrooms bathrooms_total_integer bathrooms_total bathrooms_full bathrooms_half living_area building_area_total year_built lot_size_acres property_type property_sub_type public_remarks description stories garage_spaces parking_total pool_features interior_features exterior_features heating cooling appliances fireplace_features community_features association_fee association_fee_frequency latitude longitude listing_status standard_status'.split(' '));
export function selectedPropertyContext(property, depth = 0) {
  if (!property) return null;
  return Object.fromEntries(Object.entries(property).flatMap(([key, value]) => {
    if (depth === 0 && ['location', 'features', 'associations'].includes(key) && value && typeof value === 'object' && !Array.isArray(value)) return [[key, selectedPropertyContext(value, depth + 1)]];
    if (!publicFields.has(key)) return [];
    if (value == null || typeof value === 'boolean' || (typeof value === 'number' && Number.isFinite(value))) return [[key, value]];
    if (typeof value === 'string') return [[key, value.slice(0, ['public_remarks', 'description'].includes(key) ? 5000 : 600)]];
    if (Array.isArray(value)) return [[key, value.filter(v => typeof v === 'string' || typeof v === 'number').slice(0, 30).map(v => String(v).slice(0, 150))]];
    return [];
  }));
}

export function visiblePropertyPage(message, size = 6) {
  const start = (message?.currentPage || 0) * size;
  return (message?.properties || []).slice(start, start + size);
}
